import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Image, Wand2 } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

interface GenerateVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AIModel {
  id: string;
  name: string;
  image: string;
  type: string;
}

const GenerateVideoModal: React.FC<GenerateVideoModalProps> = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const [selectedModel, setSelectedModel] = useState<AIModel | null>(null);
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);

  // Mock AI models data - in real app this would come from API
  const aiModels = [
    { id: '1', name: 'Modelo IA 1', image: '/placeholder-model.jpg', type: 'Producto' },
    { id: '2', name: 'Modelo IA 2', image: '/placeholder-model.jpg', type: 'Persona' },
    { id: '3', name: 'Modelo IA 3', image: '/placeholder-model.jpg', type: 'Objeto' },
    { id: '4', name: 'Modelo IA 4', image: '/placeholder-model.jpg', type: 'Escena' },
  ];

  const handleModelSelect = (model: AIModel) => {
    setSelectedModel(model);
  };

  const handleGenerate = async () => {
    if (!selectedModel) {
        alert(t('selectModel'));
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);

    // Simulate generation progress
    const interval = setInterval(() => {
      setGenerationProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsGenerating(false);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {t('generateVideoWithAI')}
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Model Selection */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Selecciona un modelo IA
                </h3>
                
                {/* Selected Model */}
                {selectedModel && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Modelo seleccionado
                    </h4>
                    <div className="flex items-center space-x-4 bg-primary-100 dark:bg-primary-900/30 rounded-xl p-4">
                      <div className="w-16 h-16 bg-gray-200 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                        <Image className="w-8 h-8 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{selectedModel.name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-300">{selectedModel.type}</p>
                      </div>
                      <button
                        onClick={() => setSelectedModel(null)}
                        className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Available Models */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {aiModels.map((model) => (
                    <button
                      key={model.id}
                      onClick={() => handleModelSelect(model)}
                      className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                        selectedModel?.id === model.id
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-300 dark:border-slate-600 hover:border-primary-500 hover:bg-gray-50 dark:hover:bg-slate-700'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-gray-200 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                          <Image className="w-8 h-8 text-gray-500" />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-medium text-gray-900 dark:text-gray-100">{model.name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-300">{model.type}</p>
                        </div>
                        {selectedModel?.id === model.id && (
                          <div className="w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center">
                            <span className="text-xs">✓</span>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Prompt Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Instrucciones específicas para el prompt
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="input min-h-[100px] resize-none"
                  placeholder="Describe el tipo de video, movimiento, duración, estilo visual, o cualquier detalle específico que quieras para el video generado..."
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Las instrucciones ayudarán a la IA a generar videos más precisos según tus necesidades.
                </p>
              </div>

              {/* Generation Progress */}
              {isGenerating && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
                    <span>Generando video con IA...</span>
                    <span>{generationProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${generationProgress}%` }}
                      className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                    />
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-slate-700">
                <button
                  onClick={onClose}
                  className="btn-secondary"
                  disabled={isGenerating}
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || !selectedModel}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Wand2 size={16} />
                  <span>{isGenerating ? 'Generando...' : 'Generar video con IA'}</span>
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GenerateVideoModal;
